import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import type RouterService from '@ember/routing/router-service';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import { Section } from 'tio-ui/components/layout';
import {
  PhArrowRight,
  PhClipboardText,
  PhUploadSimple,
  PhTrendUp,
  PhBuildings,
  PhCheckCircle,
  PhFileText,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { t } from 'ember-intl';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { RecommendationExtension as RecomendationModel } from 'tio-data/data/resources/recommendation.ext';
import PslfBorrowerFormsTable from 'tio-employee/components/pslf/borrower-forms-table';
import type { IntlService } from 'ember-intl';
import Card from 'tio-ui/components/card';
import FaqAccordion from 'tio-ui/components/faq-accordion';
import type { AccordionItem } from 'tio-ui/components/faq-accordion';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import HeroDashboard from 'tio-employee/components/pslf/hero-dashboard';
import PslfFormStatusAlerts from 'tio-employee/components/pslf/form-status-alerts';
import AnnualPromoCard from 'tio-employee/components/pslf/annual-auto-submit/promo-card';
import {
  annualAutoSubmitStatus,
  newestCurrentEmployerSubmittedAt,
} from 'tio-employee/utils/pslf/annual-auto-submit';
import type FeaturesService from 'tio-common/services/features';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import PslfRecommendations from 'tio-employee/components/pslf/recommendations';
import PslfQualifyingEmployment from 'tio-employee/components/pslf/qualifying-employment';
import PslfDebugPanel from 'tio-employee/components/reimagined/pslf/debug-panel';
import ForgivenessEstimator0726UsageHistory from 'tio-employee/components/forgiveness-estimator-0726/usage-history';

/**
 * Step row — icon-in-circle + title + description. Co-located here
 * (3 call sites in the "How it works" steps section).
 */
type CircleIntent = 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';
// Matches the canonical phosphor icon signature in
// `test-app/types/ember-phosphor-icons.d.ts` so Glint accepts every
// PhFoo without complaining about missing optional args (class/weight).
type IconComponent = ComponentLike<{
  Args: {
    size?: number | string;
    weight?: string;
    class?: string;
  };
  Element: SVGElement;
}>;

interface StepRowSignature {
  Args: {
    icon: IconComponent;
    intent: CircleIntent;
    titleKey: string;
    descriptionKey: string;
  };
}

const StepRow: TemplateOnlyComponent<StepRowSignature> = <template>
  <div class="tio-row-top-loose">
    <div class="app-tinted-circle app-tinted-circle--md app-tinted-circle--{{@intent}}">
      <@icon @size={{20}} />
    </div>
    <div>
      <Text @weight="semibold" class="mb-1">
        {{t @titleKey}}
      </Text>
      <Text @variant="muted">
        {{t @descriptionKey}}
      </Text>
    </div>
  </div>
</template>;

/**
 * Eligibility card — centered icon-in-circle + title + description.
 * Co-located here (3 call sites in the eligibility section).
 */
interface EligibilityCardSignature {
  Args: {
    icon: IconComponent;
    intent: CircleIntent;
    titleKey: string;
    descriptionKey: string;
  };
}

const EligibilityCard: TemplateOnlyComponent<EligibilityCardSignature> = <template>
  <Card @radius="card" @elevation="card" @padding="md" class="tio-stack-center text-center">
    <div class="app-tinted-circle app-tinted-circle--lg app-tinted-circle--{{@intent}} mb-3">
      <@icon @size={{24}} />
    </div>
    <Heading @level={{3}} @role="panel-title" class="mb-1">
      {{t @titleKey}}
    </Heading>
    <Text @variant="muted" @size="sm">
      {{t @descriptionKey}}
    </Text>
  </Card>
</template>;

/**
 * Route model for the PSLF landing page. Exported so a test can build one
 * without reaching into this component's private signature.
 */
export interface PslfDashboardIndexModel {
  loanSummary: LoanSummary;
  accounts: AccountModel[];
  pslfProfile: PslfProfileModel;
  pslfForms: PslfFormModel[];
  person: PersonModel;
  mostRecentRecommendation?: RecomendationModel;
  history: RecomendationModel[];
  debugLinkLoans: boolean;
}

interface S {
  Args: {
    model: PslfDashboardIndexModel;
  };
}

export default class PslfDashboardIndexComponent extends Component<S> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare features: FeaturesService;
  @service declare pslfDebug: PslfDebugService;
  @service('answer-profile') declare answerProfileService: AnswerProfileService;

  /** One resolution of the annual window, shared by the strip, the hero ring and the dialog. */
  get annualAutoSubmit() {
    const profile = this.args.model.pslfProfile;

    return annualAutoSubmitStatus({
      flagEnabled: this.features.can('pslfAnnualAutoSubmit') || this.pslfDebug.isOverridingAnnual,
      optedOut: profile?.annualAutoSubmitOptedOut ?? false,
      scheduledAt: profile?.annualAutoSubmitScheduledAt,
      priorSubmittedAt: newestCurrentEmployerSubmittedAt(this.args.model.pslfForms ?? []),
      source: profile?.annualAutoSubmitSource,
    });
  }

  answerProfileState = trackedFunction(this, async () => {
    return this.answerProfileService.findOrCreateAnswerProfileForCurrentUser();
  });

  // ?showEstimator=true forces the hero's forgiveness estimator tile to show
  // regardless of the usual gating (forms / NSLDS / annual reminder). Within the
  // tile the existing recommendation-vs-CTA logic still applies.
  get forceEstimator() {
    return this.router.currentRoute?.queryParams?.['showEstimator'] === 'true';
  }

  get answerProfile() {
    return this.answerProfileState.value ?? undefined;
  }

  get hasPslfForms() {
    return (this.args.model.pslfForms?.length ?? 0) > 0;
  }

  get debugLinkLoans() {
    return this.args.model.debugLinkLoans ?? false;
  }

  get hasNsldsData() {
    return (this.args.model.person.accounts ?? []).some((account) => account.hasNsldsPslfData);
  }

  get hasActiveRecommendations() {
    const summaries = this.args.model.loanSummary?.summaryByLoanType || [];
    const allPlans = summaries.flatMap((s) => s.repaymentPlans);
    const hasMissing = allPlans.some((p) => p === 'noRepaymentPlan' || !p);

    // In-platform recommendations can't be checked off — always active if present
    const shouldLinkLoans = this.debugLinkLoans || !this.hasNsldsData;
    if (shouldLinkLoans || hasMissing) return true;

    // External recommendations — active only if not checked off by user
    const profile = this.answerProfile;
    const shouldConsolidate = summaries.some((s) => s.loanType !== 'direct');
    const validPlans = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const shouldChangeRepayment =
      !hasMissing &&
      allPlans.length > 0 &&
      [...new Set(allPlans)].every((p) => !validPlans.includes(p));
    const needsToRecertify = !!this.args.model.mostRecentRecommendation;

    if (shouldConsolidate && !profile?.consolidatedLoansAt) return true;
    if (shouldChangeRepayment && !profile?.changedRepaymentPlanAt) return true;
    if (needsToRecertify && !profile?.recertifiedIncomeAt) return true;

    return false;
  }

  get faqItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('pslf.reimagined.faqs.what_is_pslf.question'),
        answer: this.intl.t('pslf.reimagined.faqs.what_is_pslf.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.question'),
        answer: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.qualifying_employers.question'),
        answer: this.intl.t('pslf.reimagined.faqs.qualifying_employers.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.how_to_apply.question'),
        answer: this.intl.t('pslf.reimagined.faqs.how_to_apply.answer'),
      },
    ];
  }

  <template>
    <HeroDashboard
      @pslfProfile={{@model.pslfProfile}}
      @pslfForms={{@model.pslfForms}}
      @loanSummary={{@model.loanSummary}}
      @person={{@model.person}}
      @forceEstimator={{this.forceEstimator}}
      @annualAutoSubmit={{this.annualAutoSubmit}}
    />

    {{#if this.hasPslfForms}}
      <AnnualPromoCard @status={{this.annualAutoSubmit}} @profile={{@model.pslfProfile}} />
    {{/if}}

    {{#if this.hasPslfForms}}
      {{#if this.hasActiveRecommendations}}
        <PslfRecommendations
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @recommendation={{@model.mostRecentRecommendation}}
          @debugLinkLoans={{this.debugLinkLoans}}
          @answerProfile={{this.answerProfile}}
        />
      {{/if}}
    {{/if}}

    {{#if this.hasPslfForms}}
      <PslfFormStatusAlerts
        @forms={{@model.pslfForms}}
        @annualAutoSubmit={{this.annualAutoSubmit}}
      />
    {{/if}}

    {{#if this.hasPslfForms}}
      <Section class="overflow-x-auto">
        <:header>
          {{t "pslf.document_repo.my_pslf_forms"}}
        </:header>
        <:actions>
          <Button
            @appearance="outlined"
            @intent="primary"
            @size="sm"
            data-test-pslf-start-new-form
            {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
          >
            {{t "pslf.reimagined.submit_new_form"}}
            <PhArrowRight @size={{14}} />
          </Button>
        </:actions>
        <:body>
          <PslfBorrowerFormsTable @forms={{@model.pslfForms}} />
        </:body>
      </Section>
    {{else}}
      <Section>
        <:header>
          {{t "pslf.reimagined.steps.heading"}}
        </:header>
        <:body>
          <div class="space-y-6">
            <StepRow
              @icon={{PhClipboardText}}
              @intent="brand"
              @titleKey="pslf.reimagined.steps.submit_form.title"
              @descriptionKey="pslf.reimagined.steps.submit_form.description"
            />
            <StepRow
              @icon={{PhUploadSimple}}
              @intent="info"
              @titleKey="pslf.reimagined.steps.upload_data.title"
              @descriptionKey="pslf.reimagined.steps.upload_data.description"
            />
            <StepRow
              @icon={{PhTrendUp}}
              @intent="brand"
              @titleKey="pslf.reimagined.steps.track_progress.title"
              @descriptionKey="pslf.reimagined.steps.track_progress.description"
            />
          </div>
        </:body>
      </Section>
    {{/if}}

    {{#if this.hasPslfForms}}
      <PslfQualifyingEmployment @forms={{@model.pslfForms}} />
    {{else}}
      <Section>
        <:header>
          {{t "pslf.reimagined.eligibility.heading"}}
        </:header>
        <:body>
          <div class="app-card-grid app-card-grid--medium">
            <EligibilityCard
              @icon={{PhBuildings}}
              @intent="brand"
              @titleKey="pslf.reimagined.eligibility.qualifying_employer.title"
              @descriptionKey="pslf.reimagined.eligibility.qualifying_employer.description"
            />
            <EligibilityCard
              @icon={{PhCheckCircle}}
              @intent="info"
              @titleKey="pslf.reimagined.eligibility.payments.title"
              @descriptionKey="pslf.reimagined.eligibility.payments.description"
            />
            <EligibilityCard
              @icon={{PhFileText}}
              @intent="warning"
              @titleKey="pslf.reimagined.eligibility.direct_loans.title"
              @descriptionKey="pslf.reimagined.eligibility.direct_loans.description"
            />
          </div>
        </:body>
      </Section>
    {{/if}}

    {{#if this.hasPslfForms}}
      {{#unless this.hasActiveRecommendations}}
        <PslfRecommendations
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @recommendation={{@model.mostRecentRecommendation}}
          @debugLinkLoans={{this.debugLinkLoans}}
          @answerProfile={{this.answerProfile}}
        />
      {{/unless}}
    {{/if}}

    <Section>
      <:header>
        {{t "pslf.reimagined.faqs.heading"}}
      </:header>
      <:body>
        <FaqAccordion @items={{this.faqItems}} />
      </:body>
    </Section>

    {{#if @model.history.length}}
      <ForgivenessEstimator0726UsageHistory @recommendations={{@model.history}} />
    {{/if}}

    <PslfDebugPanel />
  </template>
}
